// components/create/MusicSection.js
//
// The playlist that plays on the invitation.
//
// ── The bug this fixes ─────────────────────────────────────────────────────
//
// BasicSection had two bare URL boxes, "Spotify Playlist URL" and "YouTube
// Playlist URL", and the create form saved both. But the invitation page reads
// them through `selected_music_service`:
//
//     event.selected_music_service === 'youtube' ? youtubePlaylistId(...) : null
//
// and the website never wrote that column. So a playlist pasted on the web was
// stored, and then never rendered — the host saw their link accepted and their
// guests heard nothing. Picking the service is what makes the link real, so it
// is now a choice rather than a hidden column.
//
// ── Spotify is parked ──────────────────────────────────────────────────────
//
// Same as the app's src/features/create/musicServices.js: Spotify playback
// needs the GUEST to have Premium, so for most people an invitation with a
// Spotify playlist is silence. It stays selectable only on an invitation that
// already has it, so nothing existing breaks.
//
// ── Why the public check ───────────────────────────────────────────────────
//
// A private or unlisted YouTube playlist plays for its owner and for nobody
// else — the host tests it, hears music, shares the invitation, and every
// guest gets an error. The app warns about exactly this when a host picks a
// non-public playlist from their account. The web has no OAuth session to ask
// YouTube with, so it checks the only way a browser can: load the playlist's
// embed and see whether it is watchable.

import React, { useState } from 'react';
import YouTubePlaylistPicker from './YouTubePlaylistPicker';
import { isConfigured as youtubeAuthConfigured } from '../../services/youtubeService';
import { FaSpotify, FaYoutube } from 'react-icons/fa';
import { getYouTubePlaylistId, getYouTubePlaylistEmbedUrl } from '../../utils/youtubeUtils';
import '../../styles/MusicSection.scss';
import Icon from '../Icon';

const SPOTIFY_PLAYLIST = /open\.spotify\.com\/playlist\/([A-Za-z0-9]+)/;

export default function MusicSection({
  selectedMusicService,
  setSelectedMusicService,
  playlist,
  setPlaylist,
  youtubePlaylist,
  setYoutubePlaylist,
}) {
  const [checked, setChecked] = useState(null); // null | 'public' | 'restricted'
  const [checking, setChecking] = useState(false);
  const [showPicker, setShowPicker] = useState(false);

  const youtubeId = getYouTubePlaylistId(youtubePlaylist);
  const spotifyMatch = playlist ? playlist.match(SPOTIFY_PLAYLIST) : null;
  const spotifyId = spotifyMatch ? spotifyMatch[1] : null;
  const spotifyOk = !playlist || !!spotifyMatch;

  // Spotify is offered only where it is already in use.
  const spotifyAvailable = selectedMusicService === 'spotify';

  const select = (service) => {
    setSelectedMusicService(service === selectedMusicService ? null : service);
    setChecked(null);
  };

  /**
   * Does this playlist actually play, for someone who is not signed in?
   *
   * There is no API key in this bundle and no OAuth session, so the only
   * instrument available is the embed itself — which is also the most honest
   * one, because it is literally the thing a guest will load.
   *
   * YouTube serves a normal player for a public playlist and an error page
   * for one that is private, unlisted or gone. Cross-origin rules stop a
   * script reading either, but a frame YouTube refuses to embed never fires
   * `load` at all, and that is enough to tell the two apart. The host also
   * sees the result directly: the player below either plays their playlist or
   * shows YouTube's own message.
   */
  const checkPublic = () => {
    if (!youtubeId) return;
    setChecking(true);
    setChecked(null);

    const frame = document.createElement('iframe');
    frame.style.cssText = 'position:absolute;width:1px;height:1px;opacity:0;pointer-events:none';
    frame.src = getYouTubePlaylistEmbedUrl(youtubeId);

    let settled = false;
    const finish = (verdict) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      frame.remove();
      setChecking(false);
      setChecked(verdict);
    };

    frame.onload = () => finish('public');
    frame.onerror = () => finish('restricted');
    // Nothing back in six seconds is treated as a failure rather than leaving
    // a spinner running for ever.
    const timer = setTimeout(() => finish('restricted'), 6000);

    document.body.appendChild(frame);
  };

  return (
    <div className="section-card music-section">
      <label className="section-title">Music</label>
      <p className="section-description">
        A playlist that plays on the invitation. Optional.
      </p>

      <div className="music-choices">
        <button
          type="button"
          className={`music-choice ${selectedMusicService === 'youtube' ? 'is-active' : ''}`}
          onClick={() => select('youtube')}
        >
          <FaYoutube style={{ color: '#FF0000' }} />
          <span>
            <strong>YouTube</strong>
            <small>Free for all guests</small>
          </span>
          {selectedMusicService === 'youtube' && <Icon name="checkmark-circle" size={18} />}
        </button>

        {spotifyAvailable && (
          <button
            type="button"
            className={`music-choice ${selectedMusicService === 'spotify' ? 'is-active' : ''}`}
            onClick={() => select('spotify')}
          >
            <FaSpotify style={{ color: '#1DB954' }} />
            <span>
              <strong>Spotify</strong>
              <small>Only guests with Premium can hear it</small>
            </span>
            {selectedMusicService === 'spotify' && <Icon name="checkmark-circle" size={18} />}
          </button>
        )}
      </div>

      {selectedMusicService === 'youtube' && (
        <div className="input-group">
          {/* Sign in, if this site has a Google Web client configured.
              Signed in, the host picks from their own playlists, sees each
              one's privacy, can make a new one (public, so guests can
              actually play it) and add songs — rather than pasting a link and
              hoping. Without the client id the button is not shown at all: a
              sign-in that cannot sign in is worse than none. */}
          {youtubeAuthConfigured() && (
            <button
              type="button"
              className="music-signin"
              onClick={() => setShowPicker(true)}
            >
              <FaYoutube style={{ color: '#FF0000' }} />
              Choose from my YouTube playlists
            </button>
          )}

          <label className="field-label" htmlFor="yt-playlist">
            {youtubeAuthConfigured() ? 'Or paste a playlist link' : 'YouTube playlist link'}
          </label>
          <input
            id="yt-playlist"
            type="url"
            inputMode="url"
            className="text-input"
            placeholder="https://www.youtube.com/playlist?list=…"
            value={youtubePlaylist || ''}
            onChange={(e) => { setYoutubePlaylist(e.target.value); setChecked(null); }}
          />

          {youtubePlaylist && !youtubeId && (
            <p className="music-error">
              No playlist in that link. A playlist URL has a <code>list=</code> in it.
            </p>
          )}

          {youtubeId && (
            <>
              <button
                type="button"
                className="music-check"
                onClick={checkPublic}
                disabled={checking}
              >
                {checking ? 'Checking…' : 'Check guests can play it'}
              </button>

              {checked === 'public' && (
                <p className="music-ok">
                  <Icon name="checkmark-circle" size={14} />
                  Looks playable. Guests should hear it.
                </p>
              )}

              {checked === 'restricted' && (
                <p className="music-warn">
                  YouTube would not play this playlist here, which usually means it
                  is private or unlisted. Guests would get an error. In YouTube,
                  open the playlist, choose Edit, and set its visibility to Public.
                </p>
              )}

              {/* The playlist itself, in the form.
                  This is what "actually works" means without an OAuth flow:
                  the host is not asked to trust that their link was accepted,
                  they can see the videos, play them, and watch the same
                  player their guests will get. A playlist that is private or
                  deleted shows YouTube's own error here rather than looking
                  fine until someone else opens the invitation. */}
              <div className="music-embed">
                <iframe
                  title="Playlist preview"
                  src={getYouTubePlaylistEmbedUrl(youtubeId)}
                  allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
              <p className="music-hint">
                This is the player your guests will see. If it plays here, it plays
                for them.
              </p>
            </>
          )}
        </div>
      )}

      {showPicker && (
        <YouTubePlaylistPicker
          onPick={(url) => { setYoutubePlaylist(url); setChecked('public'); }}
          onClose={() => setShowPicker(false)}
        />
      )}

      {selectedMusicService === 'spotify' && (
        <div className="input-group">
          <label className="field-label" htmlFor="sp-playlist">Spotify playlist link</label>
          <input
            id="sp-playlist"
            type="url"
            inputMode="url"
            className="text-input"
            placeholder="https://open.spotify.com/playlist/…"
            value={playlist || ''}
            onChange={(e) => setPlaylist(e.target.value)}
          />
          {!spotifyOk && (
            <p className="music-error">That does not look like a Spotify playlist link.</p>
          )}

          {spotifyOk && spotifyId && (
            <div className="music-embed music-embed--spotify">
              <iframe
                title="Spotify playlist preview"
                src={`https://open.spotify.com/embed/playlist/${spotifyId}?theme=0`}
                allow="clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
              />
            </div>
          )}
          <p className="music-warn">
            Spotify playback needs the guest to have Premium, so most people will
            hear nothing. YouTube works for everyone.
          </p>
        </div>
      )}
    </div>
  );
}
